/**
 * Enhanced Billing Analytics Dashboard
 * 
 * Provides comprehensive billing insights for admin users:
 * - Real-time revenue metrics
 * - Subscription health monitoring
 * - Payment failure tracking
 * - Churn analysis and prevention
 * - Growth forecasting
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { useQuery } from 'wasp/client/operations';
import { getAdminBillingMetrics } from 'wasp/client/operations';
import {
  DollarSign,
  Users,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  Calendar,
  CreditCard,
  UserCheck,
  UserX
} from 'lucide-react';
import type { BillingHealthMetrics, BillingAlert } from '../../server/billing/billingMonitoring';

type AlertItem = BillingAlert & {
  message?: string;
  level?: 'info' | 'warn' | 'error';
  createdAt?: string;
};

interface BillingDashboardProps {
  isAdmin: boolean;
  className?: string;
}

export const BillingDashboard: React.FC<BillingDashboardProps> = ({
  isAdmin,
  className = ''
}) => {
  const [timeframe, setTimeframe] = useState<'7d' | '30d' | '90d'>('30d');
  const { data: metrics, isLoading: loading, refetch } = useQuery(getAdminBillingMetrics, { timeframe }, { enabled: isAdmin });
  const m = metrics as unknown as BillingHealthMetrics | undefined;
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Auto-refresh every 5 minutes
  useEffect(() => {
    if (!isAdmin) return;
    const interval = setInterval(() => {
      refetch();
      setLastRefresh(new Date());
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [isAdmin, refetch]);

  const handleRefresh = () => {
    refetch();
    setLastRefresh(new Date());
  };

  const getAlertSeverityColor = (severity: BillingAlert['severity']) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number, decimals = 1) => {
    return `${value.toFixed(decimals)}%`;
  };

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-500">Admin access required to view billing dashboard.</p>
      </div>
    );
  }

  if (loading && !m) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }, (_, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!m) {
    return (
      <div className="p-8 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">Failed to load billing metrics</p>
        <Button onClick={() => refetch()} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Billing Dashboard</h2>
          <p className="text-gray-600">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        
        <div className="flex gap-2">
          <div className="flex border rounded-lg overflow-hidden">
            {(['7d', '30d', '90d'] as const).map((period) => (
              <button
                key={period}
                onClick={() => setTimeframe(period)}
                className={`px-3 py-1 text-sm ${
                  timeframe === period 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
          
          <Button 
            onClick={() => refetch()} 
            disabled={loading}
            variant="outline"
            size="sm"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Alerts */}
      {metrics.alerts.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="w-5 h-5" />
              Active Alerts ({metrics.alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {metrics.alerts.map((alert: AlertItem) => (
              <div key={alert.id} className="flex items-center justify-between p-3 bg-white rounded border">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={getAlertSeverityColor(alert.severity) as any}>
                      {alert.severity.toUpperCase()}
                    </Badge>
                    <h4 className="font-semibold">{alert.title}</h4>
                  </div>
                  <p className="text-sm text-gray-600">{alert.description}</p>
                </div>
                {alert.actionRequired && (
                  <Button size="sm" variant="outline">
                    Take Action
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-full">
                <DollarSign className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Revenue ({timeframe})</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(metrics.overview.totalRevenue30d)}
                </p>
                <p className="text-sm text-green-600">
                  ARPU: {formatCurrency(metrics.overview.averageRevenuePerUser)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Subscriptions</p>
                <p className="text-2xl font-bold">
                  {metrics.overview.totalActiveSubscriptions}
                </p>
                <p className="text-sm text-blue-600">
                  +{metrics.overview.totalTrialSubscriptions} trials
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-full">
                <TrendingDown className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Churn Rate</p>
                <p className="text-2xl font-bold">
                  {formatPercentage(metrics.overview.churnRate30d)}
                </p>
                <Progress 
                  value={metrics.overview.churnRate30d} 
                  className="mt-2 h-2" 
                  max={10}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-full">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Conversion Rate</p>
                <p className="text-2xl font-bold">
                  {formatPercentage(metrics.overview.conversionRate30d)}
                </p>
                <Progress 
                  value={metrics.overview.conversionRate30d} 
                  className="mt-2 h-2" 
                  max={30}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Payment Health */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Payment Health (24h)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm">Success Rate</span>
              <span className="font-semibold text-green-600">
                {formatPercentage(metrics.paymentHealth.successRate24h)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm">Failure Rate</span>
              <span className="font-semibold text-red-600">
                {formatPercentage(metrics.paymentHealth.failureRate24h)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm">Retry Success Rate</span>
              <span className="font-semibold text-blue-600">
                {formatPercentage(metrics.paymentHealth.retrySuccessRate)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-sm">Avg Processing Time</span>
              <span className="font-semibold">
                {(metrics.paymentHealth.avgProcessingTime / 1000).toFixed(1)}s
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Health */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCheck className="w-5 h-5" />
              Subscription Health
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-3">
              <h4 className="font-medium">By Tier</h4>
              {Object.entries(m.subscriptionHealth.byTier).map(([tier, data]) => (
                <div key={tier} className="flex justify-between items-center">
                  <span className="text-sm capitalize">
                    {tier.replace('_', ' ')}
                  </span>
                  <div className="text-right">
                    <div className="font-semibold">{data.count} users</div>
                    <div className="text-sm text-gray-600">
                      {formatCurrency(data.revenue)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="pt-4 border-t">
              <div className="flex justify-between items-center">
                <span className="text-sm">Upcoming Renewals (7d)</span>
                <span className="font-semibold">
                  {m.subscriptionHealth.upcomingRenewals7d}
                </span>
              </div>
              
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-red-600">At-Risk Subscriptions</span>
                <span className="font-semibold text-red-600">
                  {m.subscriptionHealth.atRiskSubscriptions}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Provider Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Payment Provider Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(m.subscriptionHealth.byProvider).map(([provider, data]) => (
              <div key={provider} className="flex items-center justify-between p-4 border rounded">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="font-medium capitalize">{provider}</span>
                </div>
                <div className="flex gap-8 text-right">
                  <div>
                    <div className="text-sm text-gray-600">Subscriptions</div>
                    <div className="font-semibold">{data.count}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Revenue</div>
                    <div className="font-semibold">{formatCurrency(data.revenue)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Avg per User</div>
                    <div className="font-semibold">
                      {formatCurrency(data.revenue / data.count)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
